import React, { useState, useEffect } from 'react';
import { Badge, IconButton, Popover } from '@material-ui/core';
import {
  PrintOutlined as PrintIcon,
  ErrorOutline as ErrorIcon,
  Schedule as ClockIcon,
} from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import api from 'utils/api';
import { toast, blobToFileDownload } from 'utils/helpers';

const PrintManagerMenu = (props) => {
  const { t } = useTranslation();
  const [hasPrintables, setHasPrintables] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchRecords = async () => {
    setLoading(true);

    try {
      api.useGlobalLoader = false;
      const resp = await api.get('/print', { hideGlobalLoader: true });
      const data = resp.data;

      setRecords(data);
      setHasPrintables(data.length > 0);

      handleAutoshow(data);
    } catch (err) {
      console.error(err);
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchRecords();

    // Set up interval to call fetchData every 10 seconds
    const intervalId = setInterval(fetchRecords, 10000);

    const handleFetchAndOpen = async () => {
      await fetchRecords();
      setTimeout(() => {
        const btn = document.querySelector('#printables-button');
        if (btn) {
          setAnchorEl(btn);
        }
      }, 100);
    };

    window.addEventListener('fetchAndOpenPrintables', handleFetchAndOpen);

    // Cleanup interval on component unmount
    return () => {
      clearInterval(intervalId);
      window.removeEventListener('fetchAndOpenPrintables', handleFetchAndOpen);
    };
  }, []);

  const handleAutoshow = (data) => {
    const alreadyNotifiedRecords = JSON.parse(sessionStorage.getItem('alreadyNotifiedRecords')) || [];
    const newReadyRecords = data.filter((r) => r.status === 'ready' && !alreadyNotifiedRecords.includes(r._id)).map((r) => r._id);

    if (newReadyRecords.length > 0) {
      sessionStorage.setItem('alreadyNotifiedRecords', JSON.stringify([...alreadyNotifiedRecords, ...newReadyRecords]));
      document.querySelector('#printables-button').click();
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onSubmit = async (record) => {
    if (record.status !== 'ready') return;

    try {
      const { data } = await api.get(`/print/${record._id}`);
      const { fileName, contentType, content } = data;

      var bytes = new Uint8Array(content);
      const blob = new Blob([bytes], { type: contentType });

      blobToFileDownload(fileName, blob);
    } catch (err) {
      console.error(err);
      toast(t('Nepavyko išsiųsti spausdinimo užklausos'), 'error');
    }

    fetchRecords();
  };

  const onRetry = async (record) => {
    if (record.status !== 'failed') return;

    try {
      await api.get(`/print/${record._id}/retry`);
      toast(t('Spausdinimo užklausa pakartota'), 'success');
    }
    catch (err) {
      console.error(err);
      toast(t('Nepavyko pakartoti spausdinimo užklausos'), 'error');
    }

    fetchRecords();
  };

  const onRecordClick = async (e, record) => {
    e.preventDefault();
    if (record.status === 'ready') await onSubmit(record);
    if (record.status === 'failed') await onRetry(record);

    handleClose();
  };

  const onDeleteAllClick = async (e) => {
    e.preventDefault();

    if (records.find((r) => r.status === 'ready')) {
      // Confirm if there are any ready records
      if (!window.confirm(t('Ar tikrai norite pašalinti visas spausdinimo užklausas?'))) {
        return;
      }
    }

    try {
      await api.delete('/print');
    } catch (err) {
      console.error(err);
    }

    setRecords([]);
    setHasPrintables(false);
    sessionStorage.removeItem('alreadyNotifiedRecords');
    handleClose();
  }

  const renderStatusIndicator = (status) => {
    const className = `printable-status ${status}`;
    const textMap = {
      processing: t('Ruošiama'),
      ready: t('Atsisiųsti'),
      failed: t('Klaida. Bandyti dar kartą?'),
      deleted: t('Pašalinta'),
    };

    return (
      <div className={className}>{textMap[status] || '-'}</div>
    )
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  return (
    <div>
      <IconButton id="printables-button" className="" onClick={handleClick} disabled={!hasPrintables}>
        <Badge
          badgeContent={hasPrintables ? 1 : 0}
          color="primary"
          variant="dot"
          overlap="rectangular"
        >
          <PrintIcon />
        </Badge>
      </IconButton>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        {records.length > 0 && (
          <div className="printable-wrap" style={{ padding: '10px', maxWidth: '300px' }}>
            <div className="printable-header">
              <h5>{t('Spausdinimai')}</h5>

              <a href="#" onClick={onDeleteAllClick}>{t('Pašalinti visus')}</a>
            </div>

            <ul className="printable-list" style={{ listStyleType: 'none', padding: 0, margin: 0 }}>
              {records.map((record) => (
                <li className={`printable-item-${record.status}`} key={record._id} onClick={(e) => onRecordClick(e, record)}>
                  <div className="col-left" style={{ width: '80%' }}>
                    <span className="printable-link">
                      <div className="printable-ts">
                        {moment(record.createdAt).format('YYYY-MM-DD HH:mm')}
                      </div>

                      {record.name}


                      {/* {record.status === 'failed' && (
                        <div style={{ marginTop: '5px', color: '#1890ff' }}>
                          {t('Pakartoti užklausą')}
                        </div>
                      )} */}
                    </span>
                  </div>
                  <div className="col-right" style={{ width: '20%' }}>
                    {record.formNumber && <div className="form-number">{record.formNumber}</div>}
                    {record.status === 'failed' ? <ErrorIcon style={{ color: 'red' }} /> : <PrintIcon style={{ marginTop: '3px' }} />}
                  </div>

                  <div className="printable-status-wrap">
                    {renderStatusIndicator(record.status)}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Popover>
    </div>
  );
}

export default PrintManagerMenu;